import React, { useEffect, useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  Button,
} from '@duck/ui'
import { Globe } from 'lucide-react'
import { useTar } from '@/modules'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { useLocalSettings } from '@/hooks/useLocalSettings'
import { UserService } from '@duck/api-definitions'
import { useAuth } from '@/hooks/useAuth'

interface TranslationSupportInfo {
  language: UserService.UserSettingsGuiLanguageEnum
  dir: 'ltr' | 'rtl'
  title: string
  description: string
  description2: string
  accept: string
  reject: string
}

const hebrewSupport: TranslationSupportInfo = {
  language: 'he',
  dir: 'rtl',
  title: 'אנחנו תומכים בעברית!',
  description: 'ראינו שהפעלתם תרגום לעברית, אבל אנחנו לגמרי תומכים בעברית, מימין לשמאל',
  description2: 'עברו לאיזור האישי להגדיר שפה',
  accept: 'מעבר לאיזור האישי',
  reject: 'ביטול',
}

/**
 * Maps browser translation language codes to our supported app languages.
 * Chrome uses "iw" (legacy) or "he" for Hebrew. Edge sets the lang attribute to "he".
 */
const BROWSER_LANG_TO_APP_LANG: Record<string, TranslationSupportInfo> = {
  he: hebrewSupport,
  iw: hebrewSupport, // Chrome legacy code for Hebrew
}


/**
 * Detects if the browser has translated the page and returns the target language code,
 * or null if no translation is detected.
 *
 * Supported browsers:
 * - Chrome/Google Translate: adds `translated-ltr` or `translated-rtl` class + updates `lang` on <html>
 * - Edge/Microsoft Translator: adds `_msttexthash` attributes on translated elements + updates `lang` on <html>
 * - Firefox (Bergamot) and Safari: no reliable DOM signals — undetectable
 */
function detectBrowserTranslation(): string | null {
  const html = document.documentElement

  // Chrome / Google Translate
  if (
    html.classList.contains('translated-ltr') ||
    html.classList.contains('translated-rtl')
  ) {
    return html.lang || null
  }

  // Edge / Microsoft Translator
  if (document.querySelector('[_msttexthash]')) {
    return html.lang || null
  }

  return null
}

function getAppLanguageForBrowserLang(
  browserLang: string
): TranslationSupportInfo | null {
  const base = browserLang.split('-')[0]?.toLowerCase()
  if (!base) return null
  return BROWSER_LANG_TO_APP_LANG[base] ?? null
}

export const BrowserTranslationDialog: React.FC = () => {
  const { language } = useTar()
  const { isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const { getSetting, setSetting } = useLocalSettings()
  const [isOpen, setIsOpen] = useState(false)
  const [detectedLang, setDetectedLang] =
    useState<TranslationSupportInfo | null>(null)

  const routerState = useRouterState()
  const isOnUserArea = routerState.location.pathname.startsWith('/user-area')

  const checkTranslation = useCallback(() => {
    if (getSetting('browserTranslationDismissed') || !isAuthenticated || isOnUserArea) return

    const browserLang = detectBrowserTranslation()
    if (!browserLang) {
      setIsOpen(false)
      return
    }

    const appLang = getAppLanguageForBrowserLang(browserLang)
    if (!appLang) {
      setIsOpen(false)
      return
    }

    // Don't show if the app is already in the detected language
    const currentBase = language?.split('-')[0]?.toLowerCase()
    if (currentBase === appLang.language) {
      setIsOpen(false)
      return
    }

    setDetectedLang(appLang)
    setIsOpen(true)
  }, [getSetting, language, isAuthenticated, isOnUserArea])

  useEffect(() => {
    // Check on mount
    checkTranslation()

    // Watch for translation changes via MutationObserver
    const observer = new MutationObserver(() => {
      checkTranslation()
    })

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'lang'],
    })

    // Also observe body for Edge's _msttexthash attributes
    if (document.body) {
      observer.observe(document.body, {
        attributes: true,
        subtree: true,
        attributeFilter: ['_msttexthash'],
      })
    }

    return () => observer.disconnect()
  }, [checkTranslation])

  const handleAccept = () => {
    setIsOpen(false)
    navigate({ to: '/user-area', search: { highlight: 'guiLanguage' } })
  }

  const handleDismiss = () => {
    setIsOpen(false)
    setSetting('browserTranslationDismissed', true)
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleDismiss()}>
      <DialogContent width="sm" noMinHeight noCloseButton>
        {detectedLang && (
          <div dir={detectedLang.dir}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                {detectedLang.title}
              </DialogTitle>
              <DialogDescription>
                {detectedLang.description}
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-3 pb-4 pt-2">
              <p className="text-sm text-muted-foreground">
                {detectedLang.description2}
              </p>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={handleDismiss}>
                  {detectedLang.reject}
                </Button>
                <Button onClick={handleAccept}>
                  {detectedLang.accept}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
